import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { useMemo } from "react";

import {
  computeMergeContainerImpact,
  MergeContainerImpact,
  MergeStreamLike,
} from "./mergeContainerImpact";

export const MERGE_WASTE_STREAM_CONTAINERS = gql(`
  query MergeWasteStreamContainers($wasteStreamIds: [ID!]!) {
    wasteStreamContainers(wasteStreamIds: $wasteStreamIds) {
      id
      containerLabel
      wasteStream {
        id
        name
        classification
      }
    }
  }
`);

interface UseMergeContainerImpactResult {
  impact: MergeContainerImpact;
  /** Re-run the query so the impact reflects current server state. */
  refetch: () => void;
}

/**
 * Container impact for a set of streams being merged. Uses cache-and-network so
 * reopening the dialog reflects the current containers rather than a stale
 * cache from an earlier open — the merge acts on this list, so it must be
 * fresh. `enabled` is false when container tracking is off (the query is
 * flag-gated server-side, so it must be skipped, not just ignored).
 */
export function useMergeContainerImpact(
  selectedRows: MergeStreamLike[],
  wasteStreamIds: string[],
  enabled: boolean
): UseMergeContainerImpactResult {
  const { data, refetch } = useQuery(MERGE_WASTE_STREAM_CONTAINERS, {
    variables: { wasteStreamIds },
    skip: !enabled || wasteStreamIds.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const impact = useMemo(
    () => computeMergeContainerImpact(selectedRows, data?.wasteStreamContainers ?? []),
    [selectedRows, data]
  );

  return {
    impact,
    refetch: () => {
      void refetch();
    },
  };
}
